import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Papa from 'papaparse';
import { useAuth } from '../../context/AuthContext';
import { authFetch } from '../../lib/authFetch';

interface ParsedCsvRow {
  [key: string]: unknown;
  kind?: string;
  amount?: string;
  date?: string;
  category?: string;
  description?: string;
  tags?: string;
  tentative?: string;
  accountId?: string;
  fromAccountId?: string;
  toAccountId?: string;
  accountName?: string;
  fromAccountName?: string;
  toAccountName?: string;
  isRecurring?: string;
  frequency?: string;
  nextDueDate?: string;
  endDate?: string;
}

interface ImportError {
  rowIndex: number;
  message: string;
}

interface ImportResponseSummary {
  createdCount: number;
  failedCount: number;
  errors: ImportError[];
  importBatchId?: string;
}

const BLANK_HEADER_ERROR =
  'Blank column header detected (often caused by a trailing comma). Fix the CSV and try again.';
const PREVIEW_ROW_LIMIT = 10;
const DISPLAY_ERROR_LIMIT = 5;

const ImportCSVForm = () => {
  const { auth } = useAuth();
  const [isParsing, setIsParsing] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [dryRunEnabled, setDryRunEnabled] = React.useState(true);
  const [filename, setFilename] = React.useState('');
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [rows, setRows] = React.useState<ParsedCsvRow[]>([]);
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [serverMessage, setServerMessage] = React.useState<string | null>(null);
  const [preflightErrors, setPreflightErrors] = React.useState<ImportError[]>([]);
  const [dryRunResult, setDryRunResult] = React.useState<ImportResponseSummary | null>(null);
  const [importResult, setImportResult] = React.useState<ImportResponseSummary | null>(null);
  const [dryRunPassed, setDryRunPassed] = React.useState(false);

  const parseCsvFile = (file: File): Promise<{ parsedRows: ParsedCsvRow[]; parsedHeaders: string[] }> =>
    new Promise((resolve, reject) => {
      Papa.parse<ParsedCsvRow>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => (header ?? '').trim(),
        complete: (results) => {
          const fields = (results.meta?.fields ?? []).map((field) => (field ?? '').trim());
          if (fields.some((field) => !field)) {
            reject(new Error(BLANK_HEADER_ERROR));
            return;
          }
          resolve({ parsedRows: results.data, parsedHeaders: fields });
        },
        error: (error) => reject(error),
      });
    });

  const validateRow = (row: ParsedCsvRow): string | null => {
    const kind = String(row.kind || '').trim();
    const amount = String(row.amount || '').trim();
    const date = String(row.date || '').trim();
    const category = String(row.category || '').trim();
    if (!kind || !amount || !date || !category) return 'kind, amount, date, and category are required';

    const hasAccount = Boolean(String(row.accountId || '').trim() || String(row.accountName || '').trim());
    const hasFromToIds = Boolean(String(row.fromAccountId || '').trim() && String(row.toAccountId || '').trim());
    const hasFromToNames = Boolean(String(row.fromAccountName || '').trim() && String(row.toAccountName || '').trim());
    const hasFromTo = hasFromToIds || hasFromToNames;

    if (['income', 'expense', 'cardPurchase', 'adjustment'].includes(kind) && !hasAccount) {
      return 'row requires accountId or accountName';
    }
    if (['transfer', 'cardPayment'].includes(kind) && !hasFromTo) {
      return 'row requires from/to account ids or names';
    }
    return null;
  };

  const runPreflightValidation = (inputRows: ParsedCsvRow[]) => {
    const errors: ImportError[] = [];
    inputRows.forEach((row, index) => {
      const message = validateRow(row);
      if (message) {
        errors.push({ rowIndex: index, message });
      }
    });
    return errors;
  };

  const normalizeServerErrors = (errors: unknown): ImportError[] => {
    if (!Array.isArray(errors)) return [];
    return errors.map((error) => {
      const rowIndex = Number((error as { rowIndex?: unknown })?.rowIndex);
      const rawMessage = (error as { message?: unknown })?.message;
      return {
        rowIndex: Number.isFinite(rowIndex) ? rowIndex : 0,
        message: String(rawMessage || 'Unknown import error'),
      };
    });
  };

  const downloadErrorsCsv = (errors: ImportError[], prefix: string) => {
    const csvData = errors.map((error) => ({
      rowIndex: error.rowIndex + 1,
      message: error.message,
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${prefix}-errors.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const formatRowError = (error: ImportError) => `Row ${error.rowIndex + 1}: ${error.message}`;

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsParsing(true);
    setFilename(file.name);
    setParseError(null);
    setServerMessage(null);
    setPreflightErrors([]);
    setDryRunResult(null);
    setImportResult(null);
    setDryRunPassed(false);
    setRows([]);
    setHeaders([]);

    try {
      const { parsedRows, parsedHeaders } = await parseCsvFile(file);
      const hasBlankHeaderColumn = parsedRows.some((row) => Object.prototype.hasOwnProperty.call(row, ''));
      if (hasBlankHeaderColumn) {
        throw new Error(BLANK_HEADER_ERROR);
      }
      setRows(parsedRows);
      setHeaders(parsedHeaders);
      setPreflightErrors(runPreflightValidation(parsedRows));
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Import failed';
      setParseError(message);
    } finally {
      setIsParsing(false);
    }
  };

  const runImport = async (dryRun: boolean) => {
    if (!rows.length || isSubmitting) return;

    setIsSubmitting(true);
    setServerMessage(null);

    try {
      const token = auth?.token || localStorage.getItem('token');
      const requestHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) requestHeaders.Authorization = `Bearer ${token}`;

      const response = await authFetch('/api/ledger/import', {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({ rows, dryRun }),
      });

      const payload = await response.json();
      const normalized: ImportResponseSummary = {
        createdCount: Number(payload?.createdCount) || 0,
        failedCount: Number(payload?.failedCount) || 0,
        errors: normalizeServerErrors(payload?.errors),
        importBatchId:
          typeof payload?.importBatchId === 'string' && payload.importBatchId ? payload.importBatchId : undefined,
      };

      if (dryRun) {
        setDryRunResult(normalized);
        setDryRunPassed(response.ok && normalized.failedCount === 0);
      } else {
        setImportResult(normalized);
      }

      if (!response.ok) {
        setServerMessage(String(payload?.message || 'Import failed'));
      }
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Import failed';
      setServerMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDryRun = () => {
    runImport(true);
  };

  const handleImportNow = () => {
    if (!dryRunEnabled && !dryRunPassed) {
      const confirmed = window.confirm(
        'Dry run has not passed with zero errors. Importing may fail. Continue with real import?'
      );
      if (!confirmed) return;
    }
    runImport(false);
  };

  const renderErrorSummary = (title: string, errors: ImportError[], prefix: string) => {
    if (!errors.length) return null;

    const shownErrors = errors.slice(0, DISPLAY_ERROR_LIMIT);
    const remainingCount = errors.length - shownErrors.length;

    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {shownErrors.map((error) => (
          <Typography key={`${title}-${error.rowIndex}-${error.message}`} variant="body2">
            {formatRowError(error)}
          </Typography>
        ))}
        {remainingCount > 0 && <Typography variant="body2">{`+ ${remainingCount} more...`}</Typography>}
        <Button size="small" sx={{ mt: 1 }} onClick={() => downloadErrorsCsv(errors, prefix)}>
          Download errors CSV
        </Button>
      </Alert>
    );
  };

  const previewRows = rows.slice(0, PREVIEW_ROW_LIMIT);
  const dryRunHasNoErrors = dryRunPassed && dryRunResult?.failedCount === 0;
  const importDisabled = isSubmitting || rows.length === 0 || (dryRunEnabled && !dryRunHasNoErrors);
  const activeServerResult = importResult ?? dryRunResult;

  return (
    <Box id="ledger-csv-import">
      <Typography variant="h6" sx={{ mb: 1 }}>
        Ledger CSV Import
      </Typography>
      <Button component="label" disabled={isParsing || isSubmitting}>
        Choose CSV File
        <input hidden type="file" accept=".csv" onChange={handleUpload} />
      </Button>
      <FormControlLabel
        sx={{ ml: 2 }}
        control={<Switch checked={dryRunEnabled} onChange={(event) => setDryRunEnabled(event.target.checked)} />}
        label="Dry Run"
      />

      {isParsing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Parsing CSV...</Typography>
        </Box>
      )}

      {parseError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {parseError}
        </Alert>
      )}

      {filename && rows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="body2">{`Filename: ${filename}`}</Typography>
          <Typography variant="body2">{`Total rows parsed: ${rows.length}`}</Typography>
          <Typography variant="body2">{`Detected headers: ${headers.join(', ')}`}</Typography>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Preview (first 10 rows)
          </Typography>
          <TableContainer data-testid="import-preview-table-scroll" sx={{ width: '100%', overflowX: 'auto', mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  {headers.map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, rowIndex) => (
                  <TableRow key={`preview-${rowIndex + 1}`}>
                    <TableCell>{rowIndex + 1}</TableCell>
                    {headers.map((header) => (
                      <TableCell key={`${rowIndex + 1}-${header}`}>{String(row[header] ?? '')}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {renderErrorSummary('Preflight validation errors', preflightErrors, 'preflight')}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleDryRun}
          disabled={isSubmitting || rows.length === 0}
          startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Processing...' : 'Validate Import'}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleImportNow}
          disabled={importDisabled}
          startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Processing...' : 'Import Now'}
        </Button>
      </Box>

      {serverMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {serverMessage}
        </Alert>
      )}

      {activeServerResult && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2">
            {importResult ? 'Import results' : 'Dry run results'}
          </Typography>
          <Typography variant="body2">{`Created: ${activeServerResult.createdCount} | Failed: ${activeServerResult.failedCount}`}</Typography>
          {activeServerResult.importBatchId && (
            <Typography variant="body2">{`Import Batch ID: ${activeServerResult.importBatchId}`}</Typography>
          )}
          {renderErrorSummary('Server validation errors', activeServerResult.errors, 'server-import')}
          {importResult && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button variant="outlined" href="/transactions">
                View Transactions
              </Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                You can export from Transactions after import.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ImportCSVForm;
